import React, { useMemo } from "react";
import Select from "./Select";

/** Flatten <option> children (string or JSX) into a single searchable/display string. */
export function normalizeOptionLabel(children) {
  if (children == null || typeof children === "boolean") return "";
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children
      .map((child) => normalizeOptionLabel(child))
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }
  if (React.isValidElement(children)) {
    return normalizeOptionLabel(children.props?.children);
  }
  return "";
}

/**
 * Brand-themed replacement for native <select className="form-select">.
 * Use wherever option highlight must be #CE20B0 instead of browser default blue.
 */
/**
 * Open-menu width knobs (passed through to Select):
 * - dropdownMinWidth  – minimum px (default 120)
 * - dropdownMaxWidth  – maximum px (default 160); set with min to tune freely
 * - dropdownWidth     – exact px width (fixed size)
 * - searchable        – show search box in the menu
 */
const FormSelect = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  children,
  disabled,
  placeholder,
  style,
  className,
  onFocus,
  searchable = true,
  dropdownMinWidth,
  dropdownMaxWidth,
  dropdownWidth,
  ...rest
}) => {
  const options = useMemo(() => {
    return React.Children.toArray(children)
      .filter(
        (child) =>
          React.isValidElement(child) &&
          (child.type === "option" || child.type === "optgroup")
      )
      .flatMap((child) => {
        if (child.type === "optgroup") {
          return React.Children.toArray(child.props.children)
            .filter((opt) => React.isValidElement(opt) && opt.type === "option")
            .map((opt) => ({
              id: String(opt.props.value ?? ""),
              label: normalizeOptionLabel(opt.props.children),
            }));
        }
        return {
          id: String(child.props.value ?? ""),
          label: normalizeOptionLabel(child.props.children),
        };
      });
  }, [children]);

  const handleChange = (selectedId) => {
    const eventTarget = {
      name: name ?? id ?? "",
      id: id ?? name ?? "",
      value: selectedId,
    };
    onChange?.({ target: eventTarget });
    onBlur?.({ target: eventTarget });
  };

  /* Strip native/bootstrap classes — Select already paints its own border/chevron.
     Leaving input-style/form-select on the wrapper causes a double border. */
  const wrapperClass = (className || "")
    .replace(/\bform-select\b/g, "")
    .replace(/\binput-style\b/g, "")
    .replace(/\bon-brand-header\b/g, "")
    .trim();

  const emptyOption = options.find((opt) => opt.id === "");
  const defaultPlaceholder = emptyOption?.label || undefined;
  const resolvedPlaceholder =
    placeholder !== undefined ? placeholder : defaultPlaceholder;

  return (
    <div className={wrapperClass || undefined} title={rest.title}>
      <Select
        options={options}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        placeholder={resolvedPlaceholder}
        style={style}
        onFocus={onFocus}
        searchable={searchable}
        dropdownMinWidth={dropdownMinWidth}
        dropdownMaxWidth={dropdownMaxWidth}
        dropdownWidth={dropdownWidth}
        className={
          (className || "").includes("saved-filter-trigger") ||
          (className || "").includes("on-brand-header")
            ? "on-brand-header"
            : ""
        }
        {...rest}
      />
    </div>
  );
};

export default FormSelect;
